'use client'

import Link from 'next/link'
import { motion } from 'framer-motion'
import { FaArrowRight } from 'react-icons/fa'

/**
 * What You Can Scan — the breadth section.
 *
 * July 2026 editorial pass: was a 6-card icon grid; now a hairline-ruled
 * index. Same six categories, same one-line descriptions, same hub links
 * (each row targets one of our highest-traffic GSC content clusters, so
 * this section also feeds the topic hubs). The grid treatment made the
 * page read like a settings screen; rows read like a table of contents
 * for the rest of the site.
 */
interface ScanCategory {
  label: string
  sub: string
  href: string
}

const CATEGORIES: ScanCategory[] = [
  {
    label: 'Bottled water',
    sub: 'Aquafina, Dasani, Smartwater, ranked by particle count',
    href: '/blog/microplastics-in-bottled-water',
  },
  {
    label: 'Packaged food',
    sub: 'Cans, pouches, frozen meals, takeout containers',
    href: '/blog/foods-with-the-most-microplastics',
  },
  {
    label: 'Cosmetics & skincare',
    sub: 'Flag polyethylene, PEG, acrylates, dimethicone',
    href: '/microplastic-ingredient-checker',
  },
  {
    label: 'Baby products',
    sub: 'Bottles, formula, pouches, wipes, lotions',
    href: '/microplastics-pregnancy',
  },
  {
    label: 'Food containers',
    sub: 'Plastic tubs, lids, wrap, microwave-safe claims',
    href: '/blog/best-plastic-free-food-storage',
  },
  {
    label: 'Household items',
    sub: 'Cookware, laundry, kitchen sponges, cleaners',
    href: '/microplastic-free-products',
  },
]

export default function WhatYouCanScan() {
  return (
    <section className="w-full py-14 sm:py-section bg-white">
      <div className="content">
        <motion.div
          className="max-w-2xl mb-10 sm:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-forest mb-3">
            What you can scan
          </p>
          <h2 className="font-display text-[clamp(1.625rem,4vw,2.5rem)] font-bold text-ink leading-tight">
            Anything with a barcode or a packaging photo
          </h2>
          <p className="mt-3 text-base sm:text-lg text-gray-600">
            Six product categories. One scan each. Get a 0–100 microplastic risk
            score with the cited research behind it.
          </p>
        </motion.div>

        <ul className="border-b border-ink/10">
          {CATEGORIES.map((cat, i) => (
            <motion.li
              key={cat.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-30px' }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
            >
              <Link
                href={cat.href}
                className="group grid grid-cols-1 sm:grid-cols-[14rem_1fr_auto] items-baseline sm:items-center gap-x-6 gap-y-1 border-t border-ink/10 py-5 sm:py-6"
              >
                <h3 className="text-base sm:text-lg font-bold text-ink group-hover:text-forest transition-colors leading-snug">
                  {cat.label}
                </h3>
                <p className="text-sm sm:text-base text-gray-500 leading-snug">
                  {cat.sub}
                </p>
                <FaArrowRight
                  aria-hidden="true"
                  className="hidden sm:block w-3.5 h-3.5 text-ink/25 group-hover:text-forest group-hoverable:translate-x-1 transition-[color,background-color,border-color,text-decoration-color,transform]"
                />
              </Link>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  )
}
